import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  collection, 
  query, 
  orderBy, 
  limit, 
  startAfter, 
  getDocs, 
  doc, 
  deleteDoc, 
  updateDoc,
  where,
  Timestamp,
  getCountFromServer
} from 'firebase/firestore';
import { db } from '../firebase/config.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { 
  Invoice, 
  InvoiceFilters, 
  InvoiceSortOptions, 
  InvoiceStats,
  INVOICE_CONSTANTS 
} from '../types/invoice.ts';
import { Contractor } from '../types/contractor.ts';
import InvoiceCalculator from '../utils/invoiceCalculations.ts';
import InvoiceReport from './InvoiceReport.tsx';
import { 
  FileText, 
  Plus, 
  Search, 
  Filter, 
  Download, 
  Edit, 
  Trash2, 
  Eye,
  DollarSign,
  Calendar,
  Building,
  Clock,
  AlertCircle,
  CheckCircle,
  XCircle,
  Send,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Upload
} from 'lucide-react';

const ITEMS_PER_PAGE = 10;

const InvoiceManagement: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [allInvoices, setAllInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [lastDoc, setLastDoc] = useState<any>(null);
  const [firstDoc, setFirstDoc] = useState<any>(null);
  const [stats, setStats] = useState<InvoiceStats | null>(null);
  const [statusDropdowns, setStatusDropdowns] = useState<{ [key: string]: boolean }>({});
  const [editingClients, setEditingClients] = useState<{ [key: string]: boolean }>({});
  const [updatingClients, setUpdatingClients] = useState<{ [key: string]: boolean }>({});
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [contractorsLoading, setContractorsLoading] = useState(true);

  // Filters and sorting
  const [filters, setFilters] = useState<InvoiceFilters>({});
  const [sortOptions, setSortOptions] = useState<InvoiceSortOptions>({
    field: 'invoiceNumber',
    direction: 'desc'
  });
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Selection for bulk operations
  const [selectedInvoices, setSelectedInvoices] = useState<string[]>([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  
  // Report generation
  const [showReport, setShowReport] = useState(false);
  const [reportData, setReportData] = useState<Invoice[]>([]);
  
  // Contractor tabs
  const [selectedContractorId, setSelectedContractorId] = useState<string>('all');

  // Load contractors for client selection
  const loadContractors = useCallback(async () => {
    try {
      setContractorsLoading(true);
      const contractorsRef = collection(db, 'contractors');
      const contractorsQuery = query(contractorsRef, where('status', '==', 'active'), orderBy('companyName'));
      const snapshot = await getDocs(contractorsQuery);
      
      const contractorData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        // Convert Firestore timestamps to Date objects
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
        licenseExpires: doc.data().licenseExpires?.toDate(),
        insuranceExpires: doc.data().insuranceExpires?.toDate(),
        bondExpires: doc.data().bondExpires?.toDate(),
      })) as Contractor[];

      setContractors(contractorData);
    } catch (err) {
      console.error('Error loading contractors:', err);
      // Don't set error state here as it's not critical for invoice functionality
    } finally {
      setContractorsLoading(false);
    }
  }, []);

  // Helper function to check if a weekEnding matches date filters
  const checkWeekEndingDateFilter = (weekEnding: string, dateFrom?: Date, dateTo?: Date) => {
    if (!dateFrom && !dateTo) return true;
    
    // Helper to parse weekEnding into a Date object
    const parseWeekEndingDate = (weekEndingStr: string): Date | null => {
      // Handle BTC date range format (YYYY-MM-DD to YYYY-MM-DD) - use first date
      const dateRangeMatch = weekEndingStr.match(/^(\d{4}-\d{2}-\d{2})\s+to\s+(\d{4}-\d{2}-\d{2})$/);
      if (dateRangeMatch) {
        const [year, month, day] = dateRangeMatch[1].split('-').map(Number);
        return new Date(year, month - 1, day);
      }
      
      // Handle standard YYYY-MM-DD format
      if (/^\d{4}-\d{2}-\d{2}$/.test(weekEndingStr)) {
        const [year, month, day] = weekEndingStr.split('-').map(Number);
        return new Date(year, month - 1, day);
      }
      
      // Try parsing other formats
      const parsed = new Date(weekEndingStr);
      return isNaN(parsed.getTime()) ? null : parsed;
    };
    
    const weekEndingDate = parseWeekEndingDate(weekEnding);
    if (!weekEndingDate) return true; // If we can't parse it, include it
    
    if (dateFrom && weekEndingDate < dateFrom) return false;
    if (dateTo && weekEndingDate > dateTo) return false;
    
    return true;
  };

  // Load all invoices for stats calculation
  const loadAllInvoices = useCallback(async () => {
    if (!currentUser) return;

    try {
      // Load all invoices first, then filter client-side
      let allInvoicesQuery = query(
        collection(db, 'invoices'),
        orderBy('createdAt', 'desc')
      );

      // Apply only non-date filters to Firestore query
      if (filters.status) {
        allInvoicesQuery = query(allInvoicesQuery, where('status', '==', filters.status));
      }
      if (filters.clientName) {
        allInvoicesQuery = query(allInvoicesQuery, where('clientName', '>=', filters.clientName));
      }

      const allSnapshot = await getDocs(allInvoicesQuery);
      const allInvoicesData = allSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          invoiceDate: data.invoiceDate?.toDate() || new Date(),
          dueDate: data.dueDate?.toDate() || new Date(),
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as Invoice;
      });

      // Apply date filters client-side
      const filteredInvoices = allInvoicesData.filter(invoice => {
        return checkWeekEndingDateFilter(invoice.weekEnding || '', filters.dateFrom, filters.dateTo);
      });

      setAllInvoices(filteredInvoices);
      setTotalCount(filteredInvoices.length);
      setTotalPages(Math.ceil(filteredInvoices.length / ITEMS_PER_PAGE));

      // Calculate stats from filtered invoices
      const calculatedStats = InvoiceCalculator.calculateInvoiceStats(filteredInvoices);
      setStats(calculatedStats);

    } catch (err) {
      console.error('Error loading all invoices:', err);
    }
  }, [currentUser, filters]);

  // Load invoices for current page from already filtered data
  const loadInvoices = useCallback((page: number = 1) => {
    try {
      setLoading(true);
      setError(null);

      // Filter invoices by selected contractor
      let contractorFilteredInvoices = allInvoices;
      if (selectedContractorId !== 'all') {
        if (selectedContractorId === 'unassigned') {
          contractorFilteredInvoices = allInvoices.filter(invoice => 
            (!invoice.contractorId || invoice.contractorId === '') && 
            (!invoice.clientId || invoice.clientId === '')
          );
        } else {
          contractorFilteredInvoices = allInvoices.filter(invoice => 
            invoice.contractorId === selectedContractorId || 
            invoice.clientId === selectedContractorId
          );
        }
      }

      // Sort the filtered invoices based on sortOptions
      const sortedInvoices = [...contractorFilteredInvoices].sort((a, b) => {
        const direction = sortOptions.direction === 'asc' ? 1 : -1;
        
        switch (sortOptions.field) {
          case 'invoiceNumber':
            return direction * a.invoiceNumber.localeCompare(b.invoiceNumber);
          case 'jobName':
            return direction * a.jobName.localeCompare(b.jobName);
          case 'clientName':
            return direction * a.clientName.localeCompare(b.clientName);
          case 'invoiceDate':
            return direction * (a.invoiceDate.getTime() - b.invoiceDate.getTime());
          case 'dueDate':
            return direction * (a.dueDate.getTime() - b.dueDate.getTime());
          case 'totalAmount':
            return direction * (a.totalAmount - b.totalAmount);
          case 'status':
            return direction * a.status.localeCompare(b.status);
          default:
            return 0;
        }
      });

      // Apply client-side pagination
      const startIndex = (page - 1) * ITEMS_PER_PAGE;
      const endIndex = startIndex + ITEMS_PER_PAGE;
      const paginatedInvoices = sortedInvoices.slice(startIndex, endIndex);

      setInvoices(paginatedInvoices);
      setCurrentPage(page);
      setTotalCount(contractorFilteredInvoices.length);
      setTotalPages(Math.ceil(contractorFilteredInvoices.length / ITEMS_PER_PAGE));

    } catch (err) {
      console.error('Error loading invoices:', err);
      setError('Failed to load invoices. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [allInvoices, sortOptions, selectedContractorId]);

  // Initial load
  useEffect(() => {
    loadAllInvoices();
  }, [loadAllInvoices]);

  // Load invoices when allInvoices or sortOptions change
  useEffect(() => {
    if (allInvoices.length > 0) {
      loadInvoices(1);
    }
  }, [loadInvoices]);

  useEffect(() => {
    loadContractors();
  }, [loadContractors]);

  // Handle search
  const handleSearch = useCallback((term: string) => {
    setSearchTerm(term);
    if (term.trim()) {
      // Apply contractor filter first
      let contractorFiltered = allInvoices;
      if (selectedContractorId !== 'all') {
        if (selectedContractorId === 'unassigned') {
          contractorFiltered = allInvoices.filter(invoice => 
            (!invoice.contractorId || invoice.contractorId === '') && 
            (!invoice.clientId || invoice.clientId === '')
          );
        } else {
          contractorFiltered = allInvoices.filter(invoice => 
            invoice.contractorId === selectedContractorId || 
            invoice.clientId === selectedContractorId
          );
        }
      }
      
      const filtered = contractorFiltered.filter(invoice =>
        invoice.invoiceNumber.toLowerCase().includes(term.toLowerCase()) ||
        invoice.jobName.toLowerCase().includes(term.toLowerCase()) ||
        invoice.jobNumber.toLowerCase().includes(term.toLowerCase()) ||
        invoice.clientName.toLowerCase().includes(term.toLowerCase()) ||
        (invoice.contractorName && invoice.contractorName.toLowerCase().includes(term.toLowerCase()))
      );
      // Show only first page of search results
      setInvoices(filtered.slice(0, ITEMS_PER_PAGE));
      // Update pagination for search results
      setTotalCount(filtered.length);
      setTotalPages(Math.ceil(filtered.length / ITEMS_PER_PAGE));
      setCurrentPage(1);
    } else {
      // Reset to normal pagination
      loadInvoices(1);
    }
  }, [allInvoices, loadInvoices, selectedContractorId]);

  // Handle filter changes
  const handleFilterChange = (key: keyof InvoiceFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setCurrentPage(1);
  };
  
  // Handle contractor tab change
  const handleContractorTabChange = (contractorId: string) => {
    setSelectedContractorId(contractorId);
    setCurrentPage(1);
    setSearchTerm('');
  };
  
  // Get unique contractor count for each tab
  const getContractorInvoiceCount = (contractorId: string) => {
    if (contractorId === 'all') {
      return allInvoices.length;
    } else if (contractorId === 'unassigned') {
      return allInvoices.filter(invoice => 
        (!invoice.contractorId || invoice.contractorId === '') && 
        (!invoice.clientId || invoice.clientId === '')
      ).length;
    } else {
      return allInvoices.filter(invoice => 
        invoice.contractorId === contractorId || 
        invoice.clientId === contractorId
      ).length;
    }
  };


  // Handle sort changes
  const handleSortChange = (field: InvoiceSortOptions['field']) => {
    setSortOptions(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
    setCurrentPage(1);
  };

  // Handle invoice deletion
  const handleDeleteInvoice = async (invoiceId: string) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;

    try {
      await deleteDoc(doc(db, 'invoices', invoiceId));
      setInvoices(prev => prev.filter(invoice => invoice.id !== invoiceId));
      setSelectedInvoices(prev => prev.filter(id => id !== invoiceId));
    } catch (err) {
      console.error('Error deleting invoice:', err);
      setError('Failed to delete invoice. Please try again.');
    }
  };

  // Handle status update
  const handleStatusUpdate = async (invoiceId: string, newStatus: Invoice['status']) => {
    try {
      await updateDoc(doc(db, 'invoices', invoiceId), {
        status: newStatus,
        updatedAt: Timestamp.now()
      });

      setInvoices(prev => prev.map(invoice =>
        invoice.id === invoiceId
          ? { ...invoice, status: newStatus, updatedAt: new Date() }
          : invoice
      ));

      setAllInvoices(prev => prev.map(invoice =>
        invoice.id === invoiceId
          ? { ...invoice, status: newStatus, updatedAt: new Date() }
          : invoice
      ));

      // Recalculate stats
      const updatedAllInvoices = allInvoices.map(invoice =>
        invoice.id === invoiceId
          ? { ...invoice, status: newStatus, updatedAt: new Date() }
          : invoice
      );
      const calculatedStats = InvoiceCalculator.calculateInvoiceStats(updatedAllInvoices);
      setStats(calculatedStats);

      // Close dropdown
      setStatusDropdowns(prev => ({ ...prev, [invoiceId]: false }));
    } catch (err) {
      console.error('Error updating invoice status:', err);
      setError('Failed to update invoice status. Please try again.');
    }
  };

  // Toggle status dropdown
  const toggleStatusDropdown = (invoiceId: string) => {
    setStatusDropdowns(prev => ({
      ...prev,
      [invoiceId]: !prev[invoiceId]
    }));
  };

  // Close all dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setStatusDropdowns({});
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Handle pagination
  const handlePageChange = (page: number) => {
    if (searchTerm.trim()) {
      // Handle pagination for search results
      // Apply contractor filter first
      let contractorFiltered = allInvoices;
      if (selectedContractorId !== 'all') {
        if (selectedContractorId === 'unassigned') {
          contractorFiltered = allInvoices.filter(invoice => 
            (!invoice.contractorId || invoice.contractorId === '') && 
            (!invoice.clientId || invoice.clientId === '')
          );
        } else {
          contractorFiltered = allInvoices.filter(invoice => 
            invoice.contractorId === selectedContractorId || 
            invoice.clientId === selectedContractorId
          );
        }
      }
      
      const filtered = contractorFiltered.filter(invoice =>
        invoice.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.jobName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.jobNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (invoice.contractorName && invoice.contractorName.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      const startIndex = (page - 1) * ITEMS_PER_PAGE;
      const endIndex = startIndex + ITEMS_PER_PAGE;
      setInvoices(filtered.slice(startIndex, endIndex));
      setCurrentPage(page);
    } else {
      // Handle normal pagination
      loadInvoices(page);
    }
  };

  // Handle bulk operations
  const handleBulkDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selectedInvoices.length} invoices?`)) return;

    try {
      await Promise.all(
        selectedInvoices.map(id => deleteDoc(doc(db, 'invoices', id)))
      );
      setInvoices(prev => prev.filter(invoice => !selectedInvoices.includes(invoice.id)));
      setSelectedInvoices([]);
      setShowBulkActions(false);
    } catch (err) {
      console.error('Error deleting invoices:', err);
      setError('Failed to delete invoices. Please try again.');
    }
  };

  const handleBulkStatusUpdate = async (newStatus: Invoice['status']) => {
    try {
      await Promise.all(
        selectedInvoices.map(id => 
          updateDoc(doc(db, 'invoices', id), {
            status: newStatus,
            updatedAt: Timestamp.now()
          })
        )
      );

      setInvoices(prev => prev.map(invoice =>
        selectedInvoices.includes(invoice.id)
          ? { ...invoice, status: newStatus, updatedAt: new Date() }
          : invoice
      ));
      setSelectedInvoices([]);
      setShowBulkActions(false);
    } catch (err) {
      console.error('Error updating invoice statuses:', err);
      setError('Failed to update invoice statuses. Please try again.');
    }
  };

  // Handle selection
  const handleSelectInvoice = (invoiceId: string) => {
    setSelectedInvoices(prev => {
      const newSelection = prev.includes(invoiceId)
        ? prev.filter(id => id !== invoiceId)
        : [...prev, invoiceId];
      
      setShowBulkActions(newSelection.length > 0);
      return newSelection;
    });
  };

  const handleSelectAll = () => {
    if (selectedInvoices.length === invoices.length) {
      setSelectedInvoices([]);
      setShowBulkActions(false);
    } else {
      setSelectedInvoices(invoices.map(invoice => invoice.id));
      setShowBulkActions(true);
    }
  };

  // Handle report generation
  const handleGenerateReport = () => {
    const selectedInvoiceData = allInvoices.filter(invoice => 
      selectedInvoices.includes(invoice.id)
    );
    setReportData(selectedInvoiceData);
    setShowReport(true);
  };

  // Handle client updates
  const handleClientUpdate = async (invoiceId: string, contractorId: string) => {
    try {
      setUpdatingClients(prev => ({ ...prev, [invoiceId]: true }));
      
      const contractor = contractors.find(c => c.id === contractorId);
      if (!contractor) {
        setError('Selected contractor not found');
        return;
      }
      
      await updateDoc(doc(db, 'invoices', invoiceId), {
        clientId: contractor.id,
        clientName: contractor.companyName,
        clientAddress: contractor.address.street,
        clientCity: contractor.address.city,
        clientState: contractor.address.state,
        clientZip: contractor.address.zipCode,
        contractorId: contractor.id,
        contractorName: contractor.companyName,
        updatedAt: Timestamp.now()
      });

      // Update local state
      const updateInvoice = (invoice: Invoice) => invoice.id === invoiceId 
        ? { 
            ...invoice, 
            clientId: contractor.id,
            clientName: contractor.companyName,
            clientAddress: contractor.address.street,
            clientCity: contractor.address.city,
            clientState: contractor.address.state,
            clientZip: contractor.address.zipCode,
            contractorId: contractor.id,
            contractorName: contractor.companyName,
            updatedAt: new Date()
          }
        : invoice;

      setInvoices(prev => prev.map(updateInvoice));
      setAllInvoices(prev => prev.map(updateInvoice));
      setEditingClients(prev => ({ ...prev, [invoiceId]: false }));
    } catch (err) {
      console.error('Error updating client:', err);
      setError('Failed to update client. Please try again.');
    } finally {
      setUpdatingClients(prev => ({ ...prev, [invoiceId]: false }));
    }
  };

  const getContractorIdByClientName = (clientName: string): string => {
    const contractor = contractors.find(c => c.companyName === clientName);
    return contractor?.id || '';
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Format date
  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };

  // Format date in American format (MM/DD/YYYY)
  const formatDateAmerican = (date: Date | string) => {
    if (typeof date === 'string') {
      // If it's already a string, try to parse it and format it
      const parsedDate = new Date(date);
      if (!isNaN(parsedDate.getTime())) {
        return new Intl.DateTimeFormat('en-US', {
          year: 'numeric',
          month: 'numeric',
          day: 'numeric'
        }).format(parsedDate);
      }
      // If parsing fails, return the original string
      return date;
    }
    // If it's a Date object, format it
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric'
    }).format(date);
  };

  // Get status icon and color
  const getStatusDisplay = (status: Invoice['status']) => {
    switch (status) {
      case 'draft':
        return { icon: <Edit className="h-4 w-4" />, color: 'text-gray-500 bg-gray-100', label: 'Draft' };
      case 'sent':
        return { icon: <Send className="h-4 w-4" />, color: 'text-blue-500 bg-blue-100', label: 'Sent' };
      case 'paid':
        return { icon: <CheckCircle className="h-4 w-4" />, color: 'text-green-500 bg-green-100', label: 'Paid' };
      case 'overdue':
        return { icon: <AlertCircle className="h-4 w-4" />, color: 'text-red-500 bg-red-100', label: 'Overdue' };
      case 'cancelled':
        return { icon: <XCircle className="h-4 w-4" />, color: 'text-gray-500 bg-gray-100', label: 'Cancelled' };
      default:
        return { icon: <FileText className="h-4 w-4" />, color: 'text-gray-500 bg-gray-100', label: status };
    }
  };

  if (loading && invoices.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Invoice Management</h1>
          <p className="text-gray-600 mt-1">Manage and track your invoices</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => navigate('/timesheets/processor')}
            className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <FileText className="h-4 w-4" />
            Process Timesheets
          </button>
          <button
            onClick={() => navigate('/invoices/upload')}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <Upload className="h-4 w-4" />
            Upload Invoices
          </button>
          <button
            onClick={() => navigate('/invoices/excel-generator')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Generate Invoice
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Invoices</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalInvoices}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <FileText className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Amount</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.totalAmount)}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Outstanding</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.totalOutstanding)}</p>
              </div>
              <div className="p-3 bg-yellow-100 rounded-full">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Overdue</p>
                <p className="text-2xl font-bold text-gray-900">{stats.overdueInvoices}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-full">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search invoices..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            <Filter className="h-4 w-4" />
            Filters
          </button>
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={filters.status || ''}
                  onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Statuses</option>
                  <option value="draft">Draft</option>
                  <option value="sent">Sent</option>
                  <option value="paid">Paid</option>
                  <option value="overdue">Overdue</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
                <input
                  type="text"
                  placeholder="Client name"
                  value={filters.clientName || ''}
                  onChange={(e) => handleFilterChange('clientName', e.target.value || undefined)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                <input
                  type="date"
                  value={filters.dateFrom ? filters.dateFrom.toISOString().split('T')[0] : ''}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value ? new Date(e.target.value) : undefined)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                <input
                  type="date"
                  value={filters.dateTo ? filters.dateTo.toISOString().split('T')[0] : ''}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value ? new Date(e.target.value) : undefined)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="flex items-end">
                <button
                  onClick={() => {
                    setFilters({});
                    setSearchTerm('');
                  }}
                  className="w-full px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bulk Actions */}
      {showBulkActions && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <span className="text-blue-800 font-medium">
              {selectedInvoices.length} invoice{selectedInvoices.length !== 1 ? 's' : ''} selected
            </span>
            <div className="flex gap-2">
              <button
                onClick={handleGenerateReport}
                className="px-3 py-1 bg-purple-600 text-white rounded text-sm hover:bg-purple-700"
              >
                Generate Report
              </button>
              <button
                onClick={() => handleBulkStatusUpdate('sent')}
                className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
              >
                Mark as Sent
              </button>
              <button
                onClick={() => handleBulkStatusUpdate('paid')}
                className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
              >
                Mark as Paid
              </button>
              <button
                onClick={handleBulkDelete}
                className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
              >
                Delete
              </button>
              <button
                onClick={() => {
                  setSelectedInvoices([]);
                  setShowBulkActions(false);
                }}
                className="px-3 py-1 bg-gray-600 text-white rounded text-sm hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <span className="text-red-800">{error}</span>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-500 hover:text-red-700"
            >
              <XCircle className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Contractor Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 overflow-x-auto px-6 py-3" aria-label="Tabs">
            <button
              onClick={() => handleContractorTabChange('all')}
              className={`flex items-center whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                selectedContractorId === 'all'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              All Invoices
              <span className={`ml-2 py-0.5 px-2.5 rounded-full text-xs font-medium ${
                selectedContractorId === 'all' 
                  ? 'bg-blue-100 text-blue-600' 
                  : 'bg-gray-100 text-gray-900'
              }`}>
                {getContractorInvoiceCount('all')}
              </span>
            </button>
            
            {contractors.map(contractor => (
              <button
                key={contractor.id}
                onClick={() => handleContractorTabChange(contractor.id)}
                className={`flex items-center whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                  selectedContractorId === contractor.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Building className="h-4 w-4 mr-2" />
                {contractor.companyName}
                <span className={`ml-2 py-0.5 px-2.5 rounded-full text-xs font-medium ${
                  selectedContractorId === contractor.id 
                    ? 'bg-blue-100 text-blue-600' 
                    : 'bg-gray-100 text-gray-900'
                }`}>
                  {getContractorInvoiceCount(contractor.id)}
                </span>
              </button>
            ))}
            
            <button
              onClick={() => handleContractorTabChange('unassigned')}
              className={`flex items-center whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                selectedContractorId === 'unassigned'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <AlertCircle className="h-4 w-4 mr-2" />
              Unassigned
              <span className={`ml-2 py-0.5 px-2.5 rounded-full text-xs font-medium ${
                selectedContractorId === 'unassigned' 
                  ? 'bg-blue-100 text-blue-600' 
                  : 'bg-gray-100 text-gray-900'
              }`}>
                {getContractorInvoiceCount('unassigned')}
              </span>
            </button>
          </nav>
        </div>
      </div>

      {/* Invoices Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedInvoices.length === invoices.length && invoices.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSortChange('invoiceNumber')}
                >
                  Invoice #
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSortChange('jobName')}
                >
                  Job
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSortChange('clientName')}
                >
                  Client
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSortChange('invoiceDate')}
                >
                  Week Ending
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSortChange('dueDate')}
                  style={{ display: 'none' }}
                >
                  Due Date
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSortChange('totalAmount')}
                >
                  Amount
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSortChange('status')}
                  style={{ display: 'none' }}
                >
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {invoices.map((invoice) => {
                const statusDisplay = getStatusDisplay(invoice.status);
                const isOverdue = invoice.status === 'sent' && new Date() > invoice.dueDate;
                
                return (
                  <tr key={invoice.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedInvoices.includes(invoice.id)}
                        onChange={() => handleSelectInvoice(invoice.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {invoice.invoiceNumber}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{invoice.jobName}</div>
                      <div className="text-sm text-gray-500">{invoice.jobNumber}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Building className="h-4 w-4 text-gray-400 mr-2" />
                        {editingClients[invoice.id] ? (
                          <div className="flex items-center gap-2">
                            <select 
                              value={invoice.contractorId || getContractorIdByClientName(invoice.clientName)} 
                              onChange={(e) => handleClientUpdate(invoice.id, e.target.value)}
                              disabled={updatingClients[invoice.id] || contractorsLoading}
                              className="text-sm border border-gray-300 rounded px-2 py-1 min-w-[200px]"
                            >
                              <option value="">Select contractor...</option>
                              {contractors.map(contractor => (
                                <option key={contractor.id} value={contractor.id}>
                                  {contractor.companyName}
                                </option>
                              ))}
                            </select>
                            <button
                              onClick={() => setEditingClients(prev => ({ ...prev, [invoice.id]: false }))}
                              className="text-gray-500 hover:text-gray-700 text-sm"
                              disabled={updatingClients[invoice.id]}
                            >
                              Cancel
                            </button>
                            {updatingClients[invoice.id] && (
                              <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                            )}
                            {contractorsLoading && (
                              <span className="text-xs text-gray-500">Loading...</span>
                            )}
                          </div>
                        ) : (
                          <div 
                            className="text-sm text-gray-900 hover:text-blue-600 cursor-pointer hover:underline"
                            onClick={() => setEditingClients(prev => ({ ...prev, [invoice.id]: true }))}
                            title="Click to change client"
                          >
                            {invoice.contractorName || invoice.clientName}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                        <div className="text-sm text-gray-900">
                          {invoice.weekEnding ? (
                            typeof invoice.weekEnding === 'string' ? (
                              // Handle different date formats
                              (() => {
                                // Check if it's a BTC date range format (YYYY-MM-DD to YYYY-MM-DD)
                                const dateRangeMatch = invoice.weekEnding.match(/^(\d{4}-\d{2}-\d{2})\s+to\s+(\d{4}-\d{2}-\d{2})$/);
                                if (dateRangeMatch) {
                                  // Use the first date from the range
                                  const [year, month, day] = dateRangeMatch[1].split('-').map(Number);
                                  const localDate = new Date(year, month - 1, day);
                                  return formatDate(localDate);
                                }
                                
                                // If it's in standard YYYY-MM-DD format, parse it as local date to avoid timezone issues
                                if (/^\d{4}-\d{2}-\d{2}$/.test(invoice.weekEnding)) {
                                  const [year, month, day] = invoice.weekEnding.split('-').map(Number);
                                  const localDate = new Date(year, month - 1, day); // month is 0-indexed
                                  return formatDate(localDate);
                                } else {
                                  const parsedDate = new Date(invoice.weekEnding);
                                  return isNaN(parsedDate.getTime()) ? invoice.weekEnding : formatDate(parsedDate);
                                }
                              })()
                            ) : (
                              formatDate(invoice.weekEnding)
                            )
                          ) : (
                            formatDate(invoice.invoiceDate)
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" style={{ display: 'none' }}>
                      <div className={`flex items-center ${isOverdue ? 'text-red-600' : 'text-gray-900'}`}>
                        <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                        <div className="text-sm">{formatDate(invoice.dueDate)}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {formatCurrency(invoice.totalAmount)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" style={{ display: 'none' }}>
                      <div className="relative">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleStatusDropdown(invoice.id);
                          }}
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusDisplay.color} hover:opacity-80 cursor-pointer`}
                        >
                          {statusDisplay.icon}
                          <span className="ml-1">{statusDisplay.label}</span>
                          <ChevronDown className="ml-1 h-3 w-3" />
                        </button>
                        
                        {statusDropdowns[invoice.id] && (
                          <div className="absolute z-10 mt-1 w-32 bg-white rounded-md shadow-lg border border-gray-200">
                            <div className="py-1">
                              {(['draft', 'sent', 'paid', 'overdue', 'cancelled'] as const).map((status) => {
                                const statusInfo = getStatusDisplay(status);
                                return (
                                  <button
                                    key={status}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleStatusUpdate(invoice.id, status);
                                    }}
                                    className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center ${
                                      invoice.status === status ? 'bg-gray-100' : ''
                                    }`}
                                  >
                                    {statusInfo.icon}
                                    <span className="ml-2">{statusInfo.label}</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => navigate(`/invoices/${invoice.id}`)}
                          className="text-blue-600 hover:text-blue-900"
                          title="View Invoice"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => navigate(`/invoices/${invoice.id}/edit`)}
                          className="text-gray-600 hover:text-gray-900"
                          title="Edit Invoice"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteInvoice(invoice.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete Invoice"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {invoices.length === 0 && !loading && (
          <div className="text-center py-12">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No invoices found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || Object.keys(filters).length > 0
                ? 'Try adjusting your search or filters'
                : 'Get started by generating your first invoice'
              }
            </p>
            {!searchTerm && Object.keys(filters).length === 0 && (
              <div className="mt-6">
                <button
                  onClick={() => navigate('/invoices/excel-generator')}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Generate Invoice
                </button>
              </div>
            )}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{((currentPage - 1) * ITEMS_PER_PAGE) + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(currentPage * ITEMS_PER_PAGE, totalCount)}</span> of{' '}
                    <span className="font-medium">{totalCount}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="h-5 w-5" />
                      <span className="sr-only">Previous</span>
                    </button>
                    
                    {/* Page numbers */}
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            currentPage === pageNum
                              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Next</span>
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Invoice Report Modal */}
      {showReport && (
        <InvoiceReport
          invoices={reportData}
          onClose={() => {
            setShowReport(false);
            setReportData([]);
          }}
        />
      )}
      </div>
    </div>
  );
};

export default InvoiceManagement;
